import { useLazyQuery } from "@apollo/client/react";
import {
  DSButton,
  DSInput,
} from "@components/design-system/molecules";
import { GET_NOTE } from "@components/notes/EditNote";
import {
  useTeam,
  useNoteUsage,
  useAuth,
  usePreventRecordingInterruptions,
  useDarkMode,
  usePremiumFeature,
  usePreferences,
  useRecordingJobType,
  useTwilioAPI,
  useTemplates,
} from "@hooks";
import { useLiveQuery } from "dexie-react-hooks";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import moment from "moment";
import { useState, useEffect, useEffectEvent, useRef } from "react";
import { Controller } from "react-hook-form";
import {
  config,
  useTransition,
  useSpring,
  animated,
} from "react-spring";
import { v4 as uuidv4 } from "uuid";

import { RecordingAsSelection } from "./RecordingAsSelection";
import RecordingJobTypeSelection from "./RecordingJobTypeSelection";
import { RecordingSection } from "./RecordingSection";
import UnsyncedRecordings from "./UnsyncedRecordings";
import { UploadFinishedButton } from "./UploadFinishedButton";
import { uploadToCloud } from "../../../api/UploadAudio";
import { bytesToMB } from "../../../api/utils";
import { db } from "../../../db";
import UpgradeModal from "../../common/premium/UpgradeModal";
import { isUUID } from "../../dashboard/utils";
import ScribephoneDial from "../../scribephone/ScribephoneDial";
import { alert } from "../Alert";
import HighlightPulse from "../animations/HighlightPulse";
import CallRecordingToggle from "../buttons/CallRecordingToggle";
import { SimpleNoteTransferModal } from "../modals/SimpleNoteTransferModal";

const PREVENT_NAVIGATION_MESSAGE =
  "You have unsaved changes. Are you sure you want to leave this page?";

function RecordingTitle({ control, percentCompleted }) {
  return (
    <div
      className="self-center w-full min-w-[200px] flex-1"
      id="recorderTitle"
    >
      <Controller
        defaultValue={""}
        render={({ field }) => (
          <DSInput
            placeholder="Note title (optional)"
            disabled={percentCompleted < 100 && percentCompleted > 0}
            size="sm"
            label="Note Title"
            dataTestId="note-title-input"
            {...field}
          />
        )}
        control={control}
        name={"title"}
      />
    </div>
  );
}

const Record = ({
  createNewNote,
  id,
  isRecording,
  setIsRecording,
  control,
  getValues = () => ({}),
  shouldShowTitleInput = true,
  shouldUseSmallSize = false,
  shouldShowTypeDropdown = true,
  shouldShowRecordAs = true,
  shouldDisableCallMode = false,
  shouldDisableAudioUpload = false,
  reset = () => {},
  patientUUID = null,
  appendToNoteUuid = null,
}) => {
  const [uploadStatus, setUploadStatus] = useState({
    numChunksTotal: 0,
    numChunksCompleted: 0,
  });
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [noteToTransfer, setNoteToTransfer] = useState(null);
  const [shouldShowTransferModal, setShouldShowTransferModal] =
    useState(false);
  const [numFilesUploaded, setNumFilesUploaded] = useState(0);
  const [numTotalFilesToUpload, setNumTotalFilesToUpload] =
    useState(0);
  const { selectedJobType, setSelectedJobType } =
    useRecordingJobType();
  const { allTemplates } = useTemplates();
  const { userUuid } = useAuth();
  const [recordingAs, setRecordingAs] = useState(userUuid);
  const [prevRecordedAs, setPrevRecordedAs] = useState(null);

  const { currentTeam } = useTeam();
  const [getNote] = useLazyQuery(GET_NOTE, {
    fetchPolicy: "network-only",
  });
  const {
    reauthenticateActiveUser,
    isTokenExpiringSoon,
    isTokenExpired,
  } = useAuth();
  const [
    shouldShowUnsyncedRecordings,
    setShouldShowUnsyncedRecordings,
  ] = useState(false);
  const [shouldShowScribephoneDial, setShouldShowScribephoneDial] =
    useState(false);

  const {
    allowAudioFileUpload,
    transferNewNotes,
    isWebInMaintenanceMode,
    isScribephoneEnabled,
    releaseWebEnableFtueTour,
  } = useFlags();

  const { shouldUseAutoSoaps } = useNoteUsage();
  const { UserPermissions, hasFeatureAccess } = usePremiumFeature();
  const { firstTimeTourDisabled } = usePreferences();
  const [isScribephoneHighlighted, setIsScribephoneHighlighted] =
    useState(false);

  const { setShouldPreventInterruptions } =
    usePreventRecordingInterruptions();

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  // this exists to track the recording through its creation in mixpanel
  const debugRecordingRef = useRef(null);

  const darkModeEnabled = useDarkMode();

  const allItems = useLiveQuery(() =>
    db.unsyncedRecordings.toArray(),
  );

  let trackColor = darkModeEnabled[0] ? "#1A2232" : "#E4E4E7";

  const { isRecordingScribephoneMode } = useTwilioAPI();
  const isCall = isRecordingScribephoneMode;

  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const hasScribephoneAccess = hasFeatureAccess(
    UserPermissions.SCRIBEPHONE_ENABLED,
  );

  // FF is enabled, the toggle indicates its a call, they have pro access, and call mode is not disabled
  const shouldShowCallMode =
    !shouldDisableCallMode &&
    isScribephoneEnabled &&
    isCall &&
    hasScribephoneAccess;

  function resetUploadStatus() {
    setUploadStatus({
      numChunksTotal: 0,
      numChunksCompleted: 0,
    });
  }

  const percentCompleted = uploadStatus.numChunksTotal
    ? Math.round(
        (uploadStatus.numChunksCompleted * 100) /
          uploadStatus.numChunksTotal,
      )
    : 0;
  const isFinishedUploading = percentCompleted === 100;

  const greenCheckAnim = useTransition(isFinishedUploading, {
    from: { opacity: 0, transform: "scale(0.8)" },
    enter: { opacity: 1, transform: "scale(1)" },
    leave: { opacity: 0, transform: "scale(0.8)" },
    reverse: isFinishedUploading,
    config: config.wobbly,
  });

  const fadeButton = useSpring({
    config: config.wobbly,
    opacity: isFinishedUploading ? 0 : 1,
    transform: isFinishedUploading ? "scale(0.8)" : "scale(1)",
  });

  useEffect(() => {
    const eventListener = (event) => {
      if (isRecording) {
        event.preventDefault();
        return PREVENT_NAVIGATION_MESSAGE;
      }
      return null;
    };

    window.addEventListener("beforeunload", eventListener);
    return () => {
      window.removeEventListener("beforeunload", eventListener);
    };
  }, [isRecording]);

  useEffect(() => {
    if (isWebInMaintenanceMode && isRecording) {
      alert(
        "warning",
        "Maintenance mode has been activated. We will try to upload your recording. If it fails, it will be saved offline.",
      );
      // Stop recording and try to upload
      stopRecordingForMaintenance();
    }
  }, [isWebInMaintenanceMode, isRecording]);

  function pauseRecording() {
    try {
      mediaRecorderRef.current.pause();
      mixpanel.track("Pause recording", {
        recordingRef: debugRecordingRef.current,
      });
      setIsPaused(true);
    } catch (error) {
      mixpanel.track("Pause recording failed", {
        error: error.message,
        recordingRef: debugRecordingRef.current,
      });
      alert(
        "error",
        "There was an error pausing the recording. Please try again.",
      );
    }
  }

  async function startPausedRecording() {
    if (isTokenExpired) {
      // Session expired while paused — skip re-auth but still allow the
      // recording to resume locally. The audio will be saved to IndexedDB
      // when the user stops, and can be synced after re-login.
      mixpanel.track(
        "Resume with expired token, continuing locally",
        {
          recordingRef: debugRecordingRef.current,
        },
      );
    } else if (isTokenExpiringSoon) {
      // Token is still valid but close to expiry — silently refresh before
      // resuming so subsequent API calls don't fail mid-recording.
      mixpanel.track("Reauthenticating before resume", {
        recordingRef: debugRecordingRef.current,
      });
      await reauthenticateActiveUser();
    }

    try {
      mediaRecorderRef.current.resume();
      mixpanel.track("Resume recording", {
        recordingRef: debugRecordingRef.current,
      });
      setIsPaused(false);
    } catch (error) {
      mixpanel.track("Resume recording failed", {
        error: error.message,
        recordingRef: debugRecordingRef.current,
      });
      alert(
        "error",
        "There was an error resuming the recording. Please try again.",
      );
    }
  }

  async function checkAvailableDevices() {
    let audioAvailable = false;
    let videoAvailable = false;

    try {
      const devices = await Promise.race([
        // this should never take longer than a second, and if it does it's hanging forever
        navigator.mediaDevices.enumerateDevices(),
        new Promise((_, reject) => {
          setTimeout(
            () =>
              reject(
                "navigator.mediaDevices.enumerateDevices() timed out",
              ),
            5000,
          );
        }),
      ]);
      devices.forEach((device) => {
        if (device.kind === "audioinput") {
          audioAvailable = true;
        }
        if (device.kind === "videoinput") {
          videoAvailable = true;
        }
      });
    } catch (error) {
      mixpanel.track("Recording error", { error });
      alert(
        "error",
        "Failed to get media info from browser. Try restarting your browser or switching to a different one.",
      );
    }

    return { audioAvailable, videoAvailable };
  }

  async function startRecording() {
    if (isWebInMaintenanceMode) {
      alert(
        "error",
        "Cannot start recording during maintenance mode.",
      );
      return;
    }

    if (isRecording) {
      mixpanel.track(
        "Attempted to start a recording with another recording in progress",
      );
      return;
    }

    //check if the user's token is expired or will expire soon
    if (isTokenExpired || isTokenExpiringSoon) {
      await reauthenticateActiveUser();
    }
    setIsLoading(true);

    debugRecordingRef.current = uuidv4();
    mixpanel.track("Start recording", {
      recordingRef: debugRecordingRef.current,
    });

    const constraints = {
      audio: true,
      video: false,
    };

    try {
      //Check available devices before requesting the media stream
      const { audioAvailable, videoAvailable } =
        await checkAvailableDevices();

      if (!audioAvailable) {
        //Inform the user that no microphone is available
        alert(
          "error",
          "No audio input device found. Please connect a microphone.",
        );
        mixpanel.track("Recording error", {
          error: "No audio input device found",
          audioAvailable,
          videoAvailable,
          recordingRef: debugRecordingRef.current,
        });
        setIsLoading(false);
        return;
      }

      //Proceed with requesting the media stream
      let stream = null;

      try {
        stream =
          await navigator.mediaDevices.getUserMedia(constraints);
      } catch {
        alert(
          "error",
          "You need to allow microphone permissions to record audio.",
        );
        setIsLoading(false);
        return;
      }

      streamRef.current = stream;
      mediaRecorderRef.current = new MediaRecorder(stream);
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };
      mediaRecorderRef.current.start();

      mediaRecorderRef.current.onstart = function () {
        setIsRecording(true);
        setShouldPreventInterruptions(true);
        setIsLoading(false);
      };
    } catch (error) {
      const { audioAvailable, videoAvailable } =
        await checkAvailableDevices();

      mixpanel.track("Recording error", {
        error: error.message || error.name,
        audioAvailable,
        videoAvailable,
        recordingRef: debugRecordingRef.current,
      });

      alert(
        "error",
        "There was an error starting the recording. Please check your microphone and try again.",
      );
    }
  }

  function cancelRecording() {
    return stopRecording(true);
  }

  function stopMediaRecorder() {
    let mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder) {
      mixpanel.track("Attempted to stop a nonexistent recording");
    }
    mediaRecorder.stop();

    // Release the tracks, otherwise the browser will still be using the microphone
    for (const track of mediaRecorder.stream.getTracks()) {
      track.stop();
    }
  }

  function discardRecording() {
    setIsRecording(false);
    setShouldPreventInterruptions(false);
    setIsPaused(false);
    clearRefs();
  }

  function clearRefs() {
    audioChunksRef.current = [];
    streamRef.current = null;
    mediaRecorderRef.current = null;
    debugRecordingRef.current = null;
  }

  function handleIndexedDBError(error, blob, jobType) {
    const errorType = error?.name || "Unknown Error";
    let alertMessage;
    let mixpanelMessage;

    switch (errorType) {
      case "QuotaExceededError":
        alertMessage =
          "Storage limit exceeded. Please delete some recordings to free up space.";
        mixpanelMessage = "Storage limit exceeded.";
        break;

      case "SecurityError":
        alertMessage =
          "Storage access denied. Please enable cookies or site permissions.";
        mixpanelMessage = "Storage access denied.";
        break;

      default:
        alertMessage =
          "An unknown error occurred while saving the recording. Try again.";
        mixpanelMessage = error.message || "Unknown error.";
    }

    alert("error", alertMessage);

    mixpanel.track("IndexedDB error", {
      error: errorType,
      message: mixpanelMessage,
      fileSizeMB: blob ? bytesToMB(blob.size).toFixed(2) : "N/A",
      jobType,
      recordingRef: debugRecordingRef.current,
    });
  }

  async function maybePromptTransfer(noteUuid) {
    if (!noteUuid || !currentTeam?.users?.length) {
      return;
    }
    if (!transferNewNotes) {
      return;
    }
    // Don't show the transfer modal during FTUE — it would block the tour.
    // Only suppress when the tour is actually mounted (flag is on).
    if (releaseWebEnableFtueTour && firstTimeTourDisabled === false) {
      return;
    }
    // Don't show the modal if the user is recording as someone else AND
    // that person is not the same as the last person they recorded as
    if (recordingAs !== userUuid && prevRecordedAs !== recordingAs) {
      setPrevRecordedAs(recordingAs);
      return;
    }
    const { data } = await getNote({
      variables: {
        uuid: noteUuid,
      },
    });
    if (data?.note?.patient?.uuid) {
      return;
    }
    setNoteToTransfer(data.note);
    setShouldShowTransferModal(true);
  }

  async function uploadRecording() {
    const blob = new Blob(audioChunksRef.current, {
      type: "audio/wav",
    });
    const recordingRef = debugRecordingRef.current;
    const formValues = getValues();
    clearRefs();

    let id;
    let savedLocally = false;

    try {
      id = await db.unsyncedRecordings.add({
        saved_date: moment().format(),
        is_synced: false,
        note_uuid: null,
        blob,
        jobType: selectedJobType?.value,
        recordingAs,
        title: formValues.title || "",
      });
      savedLocally = true;

      mixpanel.track("Recording saved to IndexedDB", {
        recordingId: id,
        timestamp: new Date().toISOString(),
        fileSizeMB: bytesToMB(blob.size).toFixed(2),
        jobType: selectedJobType?.value,
        recordingRef,
      });
    } catch (error) {
      handleIndexedDBError(error, blob, selectedJobType?.value);
      return;
    }

    // Upload the recording to the cloud
    try {
      // Attempt uploadToCloud
      const response = await uploadToCloud({
        blob,
        uploadStatus,
        setUploadStatus,
        createNewNote,
        jobType: selectedJobType?.value,
        recordingAs,
        recordingRef,
      });

      if (response?.success) {
        maybePromptTransfer(response?.note?.uuid);
        try {
          // If upload is successful, try to delete the recording from IndexedDB
          await db.unsyncedRecordings.delete(id);

          const templateOwnerUuid = allTemplates.find(
            (t) => t.uuid === selectedJobType?.value,
          )?.user?.uuid;

          mixpanel.track("Recording uploaded successfully", {
            recordingId: id,
            timestamp: new Date().toISOString(),
            currentTeam: currentTeam?.uuid,
            jobType: isUUID(selectedJobType?.value)
              ? "template"
              : selectedJobType?.value,
            templateUuid:
              isUUID(selectedJobType?.value) &&
              selectedJobType?.value,
            templateOwnerUuid:
              isUUID(selectedJobType?.value) && templateOwnerUuid,
            templateIsOwnedByCurrentUser:
              !isUUID(selectedJobType?.value) ||
              templateOwnerUuid === userUuid,
            recordingRef,
          });
        } catch (dbError) {
          // Handle errors from IndexedDB deletion
          mixpanel.track("IndexedDB delete failed", {
            recordingId: id,
            error: dbError.message || dbError,
            recordingRef,
          });

          handleIndexedDBError(dbError, blob, selectedJobType?.value);
        }
      } else {
        // If the error has to do with uploadToCloud and not IndexedDB, handle as such
        throw new Error("Cloud upload failed.");
      }
    } catch (error) {
      // Capture errors from the cloud upload or the IndexedDB delete failure
      mixpanel.track("Recording Sync Failed", {
        recordingId: id,
        error: error.message || error,
        savedLocally,
        jobType: selectedJobType?.value,
        recordingRef,
      });

      if (savedLocally) {
        alert(
          "warning",
          "An error occurred while uploading your recording. It has been saved offline for now.",
        );
      } else {
        // Failed to save locally and didn't upload. Recording is very likely lost.
        alert(
          "warning",
          "An error occurred while saving your recording. Please contact support.",
        );
      }
    } finally {
      setIsRecording(false);
      setShouldPreventInterruptions(false);
      reset({ title: "" });
    }
  }

  const stopRecordingForMaintenance = useEffectEvent(() => {
    stopRecording(false);
  });

  function stopRecording(discard = false) {
    setIsPaused(false);
    resetUploadStatus();
    if (!mediaRecorderRef.current) {
      mixpanel.track(
        "Attempted to stop a recording with no recording",
      );
      return;
    }
    mixpanel.track("Stop Recording", {
      recordingRef: debugRecordingRef.current,
      discard,
    });
    if (discard) {
      mediaRecorderRef.current.onstop = discardRecording;
    } else {
      mediaRecorderRef.current.onstop = uploadRecording;
    }
    stopMediaRecorder();
  }

  // Updated handleFileUpload to try upload first, then fallback to local:

  const handleFileUpload = async (files) => {
    for (const file of files) {
      if (
        !file.type.startsWith("audio/") &&
        !["video/mp4", "video/webm"].includes(file.type)
      ) {
        alert("warning", `File ${file.name} is not an audio file.`);
        continue;
      }

      const blob = file.slice(0, file.size, file.type);

      try {
        // Attempt upload first
        const response = await uploadToCloud({
          blob,
          uploadStatus,
          setUploadStatus,
          createNewNote,
          jobType: selectedJobType?.value,
          isNoteFromFileUpload: true,
        });

        if (response?.success) {
          mixpanel.track("File Uploaded Successfully", {
            fileName: file.name,
            fileSizeMB: bytesToMB(file.size).toFixed(2),
            jobType: selectedJobType?.value,
          });
          alert(
            "success",
            `File ${file.name} uploaded successfully!`,
          );
        } else {
          throw new Error("Cloud upload failed.");
        }
      } catch {
        // Upload failed, fallback to local storage
        let fileId;
        try {
          fileId = await db.unsyncedRecordings.add({
            saved_date: moment().format(),
            is_synced: false,
            note_uuid: null,
            blob,
            jobType: selectedJobType?.value,
          });
          mixpanel.track("File Saved Offline", {
            fileName: file.name,
            fileId,
            fileSizeMB: bytesToMB(file.size).toFixed(2),
            jobType: selectedJobType?.value,
          });
          alert(
            "warning",
            `Failed to upload ${file.name}. It has been saved offline.`,
          );
        } catch (dbError) {
          handleIndexedDBError(dbError, blob, selectedJobType?.value);
        }
      }
      setNumFilesUploaded((prev) => prev + 1);
    }
    setNumTotalFilesToUpload(0);
    setNumFilesUploaded(0);
  };

  function handleFileInputChange(e) {
    const files = e.target.files;
    setNumTotalFilesToUpload(files.length);
    if (files?.length) {
      handleFileUpload(files);
    }
  }

  function startPhoneCall() {
    setShouldShowScribephoneDial(true);
  }

  const handleJobTypeChange = (newJobType) => {
    setSelectedJobType(newJobType);
    if (newJobType?.value === "callback") {
      setIsScribephoneHighlighted(true);
    }
  };

  return (
    <div className="h-full flex flex-col items-center relative">
      <UpgradeModal
        shouldShow={showUpgradeModal}
        setShouldShow={setShowUpgradeModal}
        featureName="Scribephone"
      />
      <div
        className="w-full flex flex-row items-start gap-2 gap-y-4 mt-1 flex-wrap"
        data-testid="note-title-and-template-boxes"
      >
        <SimpleNoteTransferModal
          shouldShowTransferModal={shouldShowTransferModal}
          setShouldShowTransferModal={setShouldShowTransferModal}
          note={noteToTransfer}
        />
        {isScribephoneEnabled && (
          <ScribephoneDial
            shouldShow={shouldShowScribephoneDial}
            setShouldShow={setShouldShowScribephoneDial}
            selectedJobType={selectedJobType}
            recordingAs={recordingAs}
            control={control}
            userUuid={userUuid}
            patientUUID={patientUUID}
            appendToNoteUuid={appendToNoteUuid}
          />
        )}

        {shouldShowTitleInput && (
          <RecordingTitle
            control={control}
            percentCompleted={percentCompleted}
          />
        )}
        {shouldShowRecordAs && currentTeam?.uuid && (
          <RecordingAsSelection
            recordingAs={recordingAs}
            setRecordingAs={setRecordingAs}
            showLabel={true}
          />
        )}
        {shouldShowTypeDropdown && shouldUseAutoSoaps && (
          <RecordingJobTypeSelection
            selectedJobType={selectedJobType}
            setSelectedJobType={handleJobTypeChange}
            showLabel={true}
          />
        )}
      </div>

      {isScribephoneEnabled && !shouldDisableCallMode && (
        <div className="flex flex-row items-center justify-center mt-12">
          <HighlightPulse
            isActive={isScribephoneHighlighted}
            shouldDisable={isCall}
            setIsActive={setIsScribephoneHighlighted}
            pulseColor="ring-green-400"
            glowColor="shadow-green-400/50"
            borderRadius="rounded-lg"
          >
            <CallRecordingToggle />
          </HighlightPulse>
        </div>
      )}

      <div
        className={`flex flex-col ${
          shouldUseSmallSize
            ? "w-44 h-44 md:h-52 md:w-52"
            : "h-60 w-60"
        } justify-center items-stretch mt-4`}
      >
        {isFinishedUploading ? (
          <UploadFinishedButton
            trackColor={trackColor}
            transitions={greenCheckAnim}
            resetUploadStatus={resetUploadStatus}
          />
        ) : (
          <animated.div
            style={fadeButton}
            className="w-full h-full flex items-center justify-center"
          >
            <RecordingSection
              id={id}
              isRecording={isRecording}
              isPaused={isPaused}
              isLoading={isLoading}
              audio={streamRef.current}
              percentCompleted={percentCompleted}
              startRecording={startRecording}
              stopRecording={stopRecording}
              pauseRecording={pauseRecording}
              cancelRecording={cancelRecording}
              startPausedRecording={startPausedRecording}
              trackColor={trackColor}
              alternativeOnClick={
                shouldShowCallMode && startPhoneCall
              }
              shouldShowCallMode={shouldShowCallMode}
            />
          </animated.div>
        )}
      </div>
      {!isRecording && !shouldDisableAudioUpload && (
        <div
          className={`flex gap-4 ${isRecording ? "mt-10" : "mt-4"}`}
        >
          {allowAudioFileUpload && (
            <div>
              <DSButton
                size="sm"
                onClick={() =>
                  document.getElementById("audio-file-input").click()
                }
                dataTestId="upload-audio-files-button"
              >
                {numTotalFilesToUpload > 0
                  ? `Currently uploading file ${numFilesUploaded + 1}/ ${numTotalFilesToUpload}`
                  : "Upload Audio Files"}
              </DSButton>
              <input
                type="file"
                hidden
                multiple
                accept="audio/*,video/mp4,video/webm"
                id="audio-file-input"
                onChange={handleFileInputChange}
              />
            </div>
          )}

          <>
            {allItems?.length > 0 && (
              <>
                <DSButton
                  size="sm"
                  onClick={() =>
                    setShouldShowUnsyncedRecordings(true)
                  }
                  dataTestId="sync-recordings-button"
                >
                  Sync Recordings
                  <div className="absolute -top-2 -right-3 h-6 min-w-[24px] px-1 bg-red-400 flex items-center justify-center rounded-full">
                    {allItems?.length}
                  </div>
                </DSButton>
                {shouldShowUnsyncedRecordings && (
                  <UnsyncedRecordings
                    shouldShowUnsyncedRecordings={
                      shouldShowUnsyncedRecordings
                    }
                    setShouldShowUnsyncedRecordings={
                      setShouldShowUnsyncedRecordings
                    }
                    createNewNote={createNewNote}
                  />
                )}
              </>
            )}
          </>
        </div>
      )}
    </div>
  );
};

export default Record;
